import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Search, FileSignature, ArrowUpRight, Send, CheckCircle2, X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function fmtEuro(n, p = 2) {
  return `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: p, maximumFractionDigits: p })} €`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}

const STATUS_STYLES = {
  draft:     { label: "Brouillon", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  sent:      { label: "Envoyé",    cls: "bg-blue-50 text-blue-700 border-blue-200" },
  accepted:  { label: "Accepté",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  refused:   { label: "Refusé",    cls: "bg-red-50 text-red-700 border-red-200" },
  expired:   { label: "Expiré",    cls: "bg-amber-50 text-amber-700 border-amber-200" },
  converted: { label: "Converti",  cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

export default function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data.quotes);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    all: quotes.length,
    draft: quotes.filter(q => q.status === "draft").length,
    sent: quotes.filter(q => q.status === "sent").length,
    accepted: quotes.filter(q => q.status === "accepted").length,
    converted: quotes.filter(q => q.status === "converted").length,
    refused: quotes.filter(q => q.status === "refused").length,
  }), [quotes]);

  const filtered = useMemo(() => {
    let arr = tab === "all" ? quotes : quotes.filter(q => q.status === tab);
    const s = query.trim().toLowerCase();
    if (s) {
      arr = arr.filter(q =>
        (q.number || "").toLowerCase().includes(s) ||
        (q.client?.name || "").toLowerCase().includes(s)
      );
    }
    return arr;
  }, [quotes, tab, query]);

  const kpis = useMemo(() => {
    const totalAmount = quotes.filter(q => ["sent", "accepted"].includes(q.status))
                              .reduce((s, q) => s + (q.total_ttc || 0), 0);
    const converted = quotes.filter(q => q.status === "converted")
                              .reduce((s, q) => s + (q.total_ttc || 0), 0);
    const rateAccepted = quotes.length
      ? Math.round((quotes.filter(q => ["accepted", "converted"].includes(q.status)).length / quotes.length) * 100)
      : 0;
    return { totalAmount, converted, rateAccepted };
  }, [quotes]);

  const createNew = async () => {
    try {
      const { data } = await api.post("/quotes", {
        client: { name: "Nouveau client", country: "France" },
        lines: [{ description: "Prestation", quantity: 1, unit_price_ht: 0, vat_rate: 20 }],
      });
      toast.success("Devis créé — édition en cours");
      navigate(`/app/quotes/${data.quote.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const convert = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert`);
      toast.success("Devis converti en facture brouillon");
      navigate(`/app/invoices/${data.invoice_id}/edit`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (q, status) => {
    try {
      await api.patch(`/quotes/${q.id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>Devis</h1>
          <p className="mt-1 text-sm text-slate-500">Créez, envoyez et convertissez vos devis en factures en un clic.</p>
        </div>
        <Button onClick={createNew} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                data-testid="quote-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Nouveau devis
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "En cours", value: fmtEuro(kpis.totalAmount, 0), icon: Send, accent: "blue" },
          { label: "Convertis", value: fmtEuro(kpis.converted, 0), icon: CheckCircle2, accent: "emerald" },
          { label: "Taux acceptation", value: `${kpis.rateAccepted}%`, icon: ArrowUpRight, accent: "violet" },
          { label: "Total devis", value: quotes.length, icon: FileSignature, accent: "slate" },
        ].map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                       transition={{ delay: i * 0.04 }}>
            <Card className="border-slate-200 bg-white rounded-xl p-5 card-hover">
              <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center mb-3",
                {
                  blue: "bg-blue-50 text-blue-700",
                  emerald: "bg-emerald-50 text-emerald-700",
                  violet: "bg-violet-50 text-violet-700",
                  slate: "bg-slate-100 text-slate-700",
                }[c.accent])}>
                <c.icon className="h-4 w-4" strokeWidth={1.75} />
              </div>
              <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">{c.label}</div>
              <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 font-mono tabular-nums"
                   style={{ fontFamily: "Manrope, sans-serif" }}>{c.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100">
            {[["all","Tous"],["draft","Brouillons"],["sent","Envoyés"],["accepted","Acceptés"],
              ["converted","Convertis"],["refused","Refusés"]].map(([k,l]) => (
              <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}
                            className="data-[state=active]:bg-white">
                {l}
                <span className="ml-2 text-[10px] tabular-nums text-slate-500">{counts[k] || 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input id="quotes-search" name="quotes-search" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher (numéro, client…)"
                  className="pl-8 w-64 bg-white border-slate-200"
                  data-testid="quotes-search" />
        </div>
      </div>

      {/* Table */}
      <Card className="border-slate-200 bg-white overflow-hidden rounded-xl">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <FileSignature className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">
              {query ? "Aucun résultat pour cette recherche." : "Aucun devis pour l'instant. Créez votre premier devis."}
            </p>
          </div>
        ) : (
          <Table data-testid="quotes-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-40">Numéro</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Émis le</TableHead>
                <TableHead>Expire</TableHead>
                <TableHead className="text-right">Total TTC</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right w-40"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((q) => {
                const st = STATUS_STYLES[q.status] || STATUS_STYLES.draft;
                return (
                  <TableRow key={q.id} data-testid={`quote-row-${q.id}`}
                             onClick={() => navigate(`/app/quotes/${q.id}`)}
                             className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <TableCell className="font-mono text-xs">
                      <Link to={`/app/quotes/${q.id}`}
                             onClick={(e) => e.stopPropagation()}
                             className="text-blue-600 hover:text-blue-800 hover:underline font-medium">
                        {q.number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-slate-800 truncate max-w-[240px]">{q.client?.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(q.issue_date)}</TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(q.expiry_date)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-medium text-slate-900">
                      {fmtEuro(q.total_ttc)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("text-[10px] uppercase", st.cls)}>
                        {st.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {q.status === "draft" && (
                          <Button size="sm" variant="ghost" onClick={() => setStatus(q, "sent")}
                            title="Marquer envoyé" data-testid={`send-${q.id}`}>
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {q.status === "sent" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => setStatus(q, "accepted")}
                              title="Marquer accepté" className="text-emerald-700 hover:bg-emerald-50"
                              data-testid={`accept-${q.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setStatus(q, "refused")}
                              title="Marquer refusé" className="text-red-700 hover:bg-red-50"
                              data-testid={`refuse-${q.id}`}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {["accepted", "sent"].includes(q.status) && (
                          <Button size="sm" variant="ghost" onClick={() => convert(q)}
                            title="Convertir en facture" className="text-violet-700 hover:bg-violet-50"
                            data-testid={`convert-${q.id}`}>
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
